import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import './ProductFeedback.css';

function ProductFeedback() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProductAndFeedback();
  }, [productId]);

  const fetchProductAndFeedback = async () => {
    try {
      // Get product details
      const productDoc = await getDoc(doc(db, 'products', productId));
      if (productDoc.exists()) {
        setProduct({ id: productDoc.id, ...productDoc.data() });
      }

      // Get all feedback for this product
      const querySnapshot = await getDocs(collection(db, 'feedback'));
      const feedbackList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.productId === productId) {
          feedbackList.push({ id: doc.id, ...data });
        }
      });
      
      // Sort by most recent first
      feedbackList.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toMillis() - a.createdAt.toMillis();
        }
        return 0;
      });
      
      setFeedback(feedbackList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product feedback:', error);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const getAverageRating = (item) => {
    const avg = (
      item.designRating +
      item.functionalityRating +
      item.performanceRating +
      item.valueRating +
      item.overallRating
    ) / 5;
    return avg.toFixed(1);
  };

  const filteredFeedback = feedback.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'high') return item.overallRating >= 4;
    if (filter === 'medium') return item.overallRating === 3;
    if (filter === 'low') return item.overallRating <= 2;
    return true;
  });

  if (loading) {
    return (
      <div className="product-feedback">
        <nav className="feedback-nav">
          <div className="nav-container">
            <div className="logo">BetaBuyers Admin</div>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </nav>
        <div className="feedback-content">
          <div className="loading">Loading feedback...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-feedback">
      <nav className="feedback-nav">
        <div className="nav-container">
          <div className="logo">BetaBuyers Admin</div>
          <div className="nav-buttons">
            <button onClick={() => navigate('/products')} className="btn-nav">Back to Products</button>
            <button onClick={() => navigate('/admin-dashboard')} className="btn-nav">Dashboard</button>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </div>
      </nav>

      <div className="feedback-content">
        <div className="feedback-header">
          <div>
            <h1>Feedback for {product?.name}</h1>
            <p>View and analyze beta tester feedback for this product</p>
          </div>
        </div>

        <div className="filter-section">
          <div className="filter-buttons">
            <button 
              className={`btn-filter ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Feedback ({feedback.length})
            </button>
            <button 
              className={`btn-filter ${filter === 'high' ? 'active' : ''}`}
              onClick={() => setFilter('high')}
            >
              High Rated (4-5★) ({feedback.filter(f => f.overallRating >= 4).length})
            </button>
            <button 
              className={`btn-filter ${filter === 'medium' ? 'active' : ''}`}
              onClick={() => setFilter('medium')}
            >
              Medium Rated (3★) ({feedback.filter(f => f.overallRating === 3).length})
            </button>
            <button 
              className={`btn-filter ${filter === 'low' ? 'active' : ''}`}
              onClick={() => setFilter('low')}
            >
              Low Rated (1-2★) ({feedback.filter(f => f.overallRating <= 2).length})
            </button>
          </div>
        </div>

        {filteredFeedback.length === 0 ? (
          <div className="no-feedback">
            <p>No feedback submissions for this product yet</p>
          </div>
        ) : (
          <div className="feedback-grid">
            {filteredFeedback.map(item => (
              <div key={item.id} className="feedback-card">
                <div className="feedback-card-header">
                  <div className="product-info">
                    <h3>{product?.name}</h3>
                    <p className="user-email">{item.email}</p>
                  </div>
                  <div className="overall-rating-badge">
                    <span className="rating-number">{item.overallRating}</span>
                    <span className="rating-stars">★</span>
                  </div>
                </div>

                <div className="feedback-date">
                  Submitted: {item.createdAt ? new Date(item.createdAt.toMillis()).toLocaleDateString() : 'N/A'}
                </div>

                <div className="ratings-breakdown">
                  <h4>Detailed Ratings</h4>
                  <div className="rating-row">
                    <span className="rating-label">Design & Aesthetics:</span>
                    <span className="rating-stars-display">{'★'.repeat(item.designRating)}{'☆'.repeat(5 - item.designRating)}</span>
                    <span className="rating-number-small">{item.designRating}/5</span>
                  </div>
                  <div className="rating-row">
                    <span className="rating-label">Functionality & Features:</span>
                    <span className="rating-stars-display">{'★'.repeat(item.functionalityRating)}{'☆'.repeat(5 - item.functionalityRating)}</span>
                    <span className="rating-number-small">{item.functionalityRating}/5</span>
                  </div>
                  <div className="rating-row">
                    <span className="rating-label">Performance & Reliability:</span>
                    <span className="rating-stars-display">{'★'.repeat(item.performanceRating)}{'☆'.repeat(5 - item.performanceRating)}</span>
                    <span className="rating-number-small">{item.performanceRating}/5</span>
                  </div>
                  <div className="rating-row">
                    <span className="rating-label">Value for Money:</span>
                    <span className="rating-stars-display">{'★'.repeat(item.valueRating)}{'☆'.repeat(5 - item.valueRating)}</span>
                    <span className="rating-number-small">{item.valueRating}/5</span>
                  </div>
                  <div className="rating-row average">
                    <span className="rating-label">Average Rating:</span>
                    <span className="rating-number-small average-number">{getAverageRating(item)}/5</span>
                  </div>
                </div>

                <div className="feedback-details">
                  <div className="feedback-field">
                    <strong>Issues Found:</strong>
                    <p>{item.issues}</p>
                  </div>
                  <div className="feedback-field">
                    <strong>Suggestions for Improvement:</strong>
                    <p>{item.suggestions}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductFeedback;